import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import { GridColDef, GridRowId } from "@mui/x-data-grid-premium";
import { SelectOption } from "../types";
import { EditFormModal } from "./EditFormModal";

export interface BaseContextMenuProps {
  open: boolean;
  event: MouseEvent | null;
  closeContextMenu: () => void;
  selectedRows: any[];
  onGridRefresh?: () => void;

  // Edit form props (optional — graceful fallback if not provided)
  columns?: GridColDef[];
  tableName?: string;
  apiRef?: React.RefObject<any>;
  onSingleUpdate?: (
    rowId: GridRowId,
    data: Record<string, any>
  ) => Promise<{ success: boolean; error?: string }>;
  onBulkSet?: (
    ids: (string | number)[],
    fields: Record<string, any>
  ) => Promise<{ success: boolean; error?: string }>;
  fieldOptions?: Record<string, SelectOption[]>;
  getRowId?: (row: any) => string | number;
  defaultEditFieldVisibility?: Record<string, boolean>;

  // Extension points for custom context menu wrappers
  children?: React.ReactNode;
  extraNodes?: React.ReactNode;
  activeCustomModal?: React.ReactNode | null;
}

export const BaseContextMenu: React.FC<BaseContextMenuProps> = ({
  open,
  event,
  closeContextMenu,
  selectedRows,
  onGridRefresh: _onGridRefresh,
  columns,
  tableName,
  apiRef,
  onSingleUpdate,
  onBulkSet,
  fieldOptions,
  getRowId,
  defaultEditFieldVisibility,
  children,
  extraNodes,
  activeCustomModal,
}) => {
  const [editModalOpen, setEditModalOpen] = useState(false);

  const hasEditFormProps =
    columns && tableName && apiRef && onSingleUpdate && onBulkSet && getRowId;

  // Keep base invisible until needed; allow modals to outlive open=false
  if (!open && !activeCustomModal && !editModalOpen) return null;

  // Custom modal from wrapper takes full control
  if (activeCustomModal) {
    return <>{activeCustomModal}</>;
  }

  if (editModalOpen) {
    if (hasEditFormProps) {
      return (
        <EditFormModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            closeContextMenu();
          }}
          columns={columns}
          selectedRows={selectedRows}
          tableName={tableName}
          apiRef={apiRef}
          onSingleUpdate={onSingleUpdate}
          onBulkSet={onBulkSet}
          fieldOptions={fieldOptions}
          getRowId={getRowId}
          defaultFieldVisibility={defaultEditFieldVisibility}
        />
      );
    }
    // Graceful fallback when edit form props are not yet wired
    return null;
  }

  const menuPosition = event
    ? { top: event.pageY - window.scrollY, left: event.pageX }
    : { top: 0, left: 0 };

  return (
    <>
      <Menu
        open={open}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition}
      >
        {selectedRows.length > 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="textSecondary">
              ({selectedRows.length}) selected
            </Typography>
          </MenuItem>
        )}
        {selectedRows.length > 0 && <Divider />}

        <MenuItem onClick={() => setEditModalOpen(true)}>
          <ListItemIcon>
            <EditOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            Edit Row{selectedRows.length > 1 ? "s" : ""}
          </ListItemText>
        </MenuItem>

        {children}
      </Menu>

      {extraNodes}
    </>
  );
};
